import { useRef, useState, useEffect, useMemo, useCallback } from 'react';

import {
  Box,
  Button,
  HStack,
  useOutsideClick,
  Textarea,
  Paragraph,
  IconButton,
  Tooltip,
} from '@vimeo/bokeh';
import { ClockFive } from '@vimeo/bokeh-icons';
import { secondsToTimecode } from '@vimeo/core/libs/date';
import { translate } from '@vimeo/i18n-runtime';

import { MAX_COMMENT_CHARACTERS } from '../../constants';
import { T } from '../../translations';
import { FooterInputWithTimestampProps, RichTextAreaRefType } from '../../types';

export const FooterInputWithTimestamp = ({
  errorMessage = '',
  isPublic,
  onSubmit,
  isLoading = false,
  richCommentComponent: RichCommentComponent,
  commentContextFields,
  isGuestModalActive = false,
  isDisabled = false,
  canInsertTimecode = false,
  timeCodeEnabled = false,
  timeCode,
  toggleTimeCode,
  onCommentStarted,
  overlayContent,
}: FooterInputWithTimestampProps) => {
  const inputAreaRef = useRef<HTMLDivElement>(null);
  const richtextAreaRef = useRef<RichTextAreaRefType>({} as RichTextAreaRefType);

  const [textAreaExpanded, setTextAreaExpanded] = useState(false);
  const [commentText, setCommentText] = useState('');

  const prevLengthRef = useRef(0);
  const trimmedCommentText = useMemo(() => commentText.trim(), [commentText]);

  // Ensure the text area is always expanded when timecode can be inserted, otherwise use normal expanded state
  const textAreaExpandedState = canInsertTimecode || textAreaExpanded;

  const onFocus = useCallback(() => {
    setTextAreaExpanded(true);
    richtextAreaRef?.current?.focusEnd();
  }, []);

  const onSaveClick = useCallback(() => {
    richtextAreaRef?.current?.onSubmit?.();
    onSubmit?.(commentText);
  }, [commentText, onSubmit]);

  // We only want to reset the text when isLoading changes, and dont want commentText to be a dependency
  useEffect(() => {
    if (isLoading) {
      setCommentText('');
    } else if (!errorMessage) {
      richtextAreaRef?.current?.clearContent?.();
      prevLengthRef.current = 0; // reset the previous length to 0 - needed to restore stop player behavior
      setTextAreaExpanded(false);
      richtextAreaRef?.current?.removeFocus?.();
    }
  }, [isLoading, errorMessage]);

  const onCancelClick = useCallback(() => {
    setTextAreaExpanded(false);
  }, []);

  useOutsideClick({
    enabled: !isGuestModalActive && !canInsertTimecode,
    ref: inputAreaRef,
    handler: onCancelClick,
  });

  const getCurrentTextLength = useCallback(() => {
    if (RichCommentComponent) {
      if (typeof richtextAreaRef?.current?.commentLength === 'function') {
        return richtextAreaRef?.current?.commentLength() ?? 0;
      }
      return 0;
    }
    return trimmedCommentText.trim().length ?? 0;
  }, [RichCommentComponent, trimmedCommentText]);

  // Detect transition from empty to non-empty
  const handleCommentTextChange = useCallback(
    (newText: string) => {
      setCommentText(newText);
      // Check transition: was 0, now > 0
      const wasEmpty = prevLengthRef.current === 0;
      const currentLength = getCurrentTextLength() ?? 0;
      // Update the ref for next time
      prevLengthRef.current = currentLength;
      // Trigger callback on empty -> non-empty transition
      if (wasEmpty && currentLength > 0 && onCommentStarted) {
        onCommentStarted();
      }
    },
    [onCommentStarted, getCurrentTextLength],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (trimmedCommentText && !(getCurrentTextLength() > MAX_COMMENT_CHARACTERS)) {
          onSaveClick();
          (e.target as HTMLTextAreaElement).blur();
        }
      }
    },
    [trimmedCommentText, onSaveClick, getCurrentTextLength],
  );

  return (
    <Box
      w="100%"
      ref={inputAreaRef}
      gap="10px"
      fontSize="text-sm"
      display="flex"
      flexDirection="column"
    >
      <Box position="relative">
        {RichCommentComponent ? (
          <RichCommentComponent
            autoFocus={false}
            richtext={commentText}
            isExpanded={textAreaExpandedState}
            isPublic={isPublic}
            placeholder={T.InputPlaceholder}
            setCommentText={handleCommentTextChange}
            onFocus={onFocus}
            richtextAreaRef={richtextAreaRef}
            commentContextFields={commentContextFields}
            onSubmitComment={onSubmit}
            isPostCommentLoading={isLoading}
            isDisabled={isDisabled}
            annotationTimeCode={
              textAreaExpandedState && timeCodeEnabled
                ? secondsToTimecode(timeCode ?? 0, canInsertTimecode)
                : ''
            }
            newStyleUI={canInsertTimecode}
          />
        ) : (
          // this is only rendered on comments widget default case + comments-ui-test case where RichCommentComponent is not provided
          <Textarea
            w="100%"
            mb={0}
            size="sm"
            rows={textAreaExpandedState ? 3 : 1}
            onFocus={onFocus}
            placeholder={T.InputPlaceholder}
            value={commentText}
            onChange={(e) => handleCommentTextChange(e.target.value)}
            resize="none"
            isInvalid={!!errorMessage || getCurrentTextLength() > MAX_COMMENT_CHARACTERS}
            onKeyDown={onKeyDown}
          />
        )}
        {overlayContent && (
          <Box position="absolute" bottom={0} left={0} right={0} zIndex={1}>
            {overlayContent}
          </Box>
        )}
      </Box>
      {textAreaExpandedState && (
        <HStack gap="100" justifyContent="space-between" alignItems="center">
          <HStack>
            {canInsertTimecode && (
              <Tooltip
                label={
                  timeCodeEnabled ? translate('Remove Timestamp') : translate('Enable Timestamp')
                }
                closeOnEsc={false}
              >
                <IconButton
                  variant={timeCodeEnabled ? 'primary' : 'tertiary'}
                  size="sm"
                  onClick={toggleTimeCode}
                  aria-label="Toggle timestamp"
                >
                  <ClockFive />
                </IconButton>
              </Tooltip>
            )}
            {errorMessage ? (
              <Paragraph size="sm" color="status-destructive-primary" py={8}>
                {errorMessage}
              </Paragraph>
            ) : (
              <Box fontSize={12} opacity={0.6} py={8} color="text-tertiary">
                {translate('Use @ to mention others')}
              </Box>
            )}
          </HStack>
          <Button
            variant="primary"
            type="submit"
            isLoading={isLoading}
            size="sm"
            zIndex={1}
            onClick={onSaveClick}
            isDisabled={
              getCurrentTextLength() === 0 || getCurrentTextLength() > MAX_COMMENT_CHARACTERS
            }
          >
            {T.SubmitComment}
          </Button>
        </HStack>
      )}
    </Box>
  );
};
